import { X, TrendingUp, TrendingDown, Clock, DollarSign } from 'lucide-react'
import { useOrderDetail } from '../../hooks/useOrders'
import { formatKpiEur } from '../../lib/formatters'

// ---------------------------------------------------------------------------
// Badge stato commessa
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<string, string> = {
  confirmed: 'Confermata',
  probable: 'Probabile',
  in_progress: 'In corso',
  delivered: 'Consegnata',
  invoiced: 'Fatturata',
  lost: 'Persa',
  cancelled: 'Annullata',
}

const STATUS_COLORS: Record<string, string> = {
  confirmed: 'bg-blue-500/20 text-blue-300',
  probable: 'bg-yellow-500/20 text-yellow-300',
  in_progress: 'bg-emerald-500/20 text-emerald-300',
  delivered: 'bg-teal-500/20 text-teal-300',
  invoiced: 'bg-violet-500/20 text-violet-300',
  lost: 'bg-red-500/20 text-red-300',
  cancelled: 'bg-gray-500/20 text-gray-400',
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[status] ?? 'bg-gray-500/20 text-gray-400'}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Riga dettaglio
// ---------------------------------------------------------------------------

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start py-2 border-b border-white/5 last:border-0">
      <span className="text-sm text-white/50">{label}</span>
      <span className="text-sm text-white/90 text-right max-w-[60%]">{value ?? '—'}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Drawer
// ---------------------------------------------------------------------------

interface OrderDetailDrawerProps {
  orderId: string | null
  onClose: () => void
}

export function OrderDetailDrawer({ orderId, onClose }: OrderDetailDrawerProps) {
  const { data: order, isLoading, error } = useOrderDetail(orderId)

  if (!orderId) return null

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-[#1a1f35] border-l border-white/10 z-50 overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-[#1a1f35] border-b border-white/10 px-6 py-4 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs text-white/40 mb-1">Dettaglio Commessa</p>
            {isLoading ? (
              <div className="h-5 w-48 bg-white/10 rounded animate-pulse" />
            ) : (
              <h2 className="text-base font-semibold text-white/90">
                {order?.external_id ?? order?.id?.slice(0, 8) ?? '—'}
              </h2>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white/90 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Contenuto */}
        <div className="px-6 py-4">
          {isLoading && (
            <div className="space-y-3 animate-pulse">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-4 bg-white/10 rounded" />
              ))}
            </div>
          )}

          {error && (
            <p className="text-sm text-red-400">Errore nel caricamento della commessa.</p>
          )}

          {order && (
            <div className="space-y-6">
              {/* Stato + service line */}
              <div className="flex flex-wrap gap-2">
                <StatusBadge status={order.status} />
                {order.service_line && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-white/10 text-white/60">
                    {order.service_line}
                  </span>
                )}
              </div>

              {/* Anagrafica */}
              <section>
                <h3 className="text-xs font-medium text-white/40 uppercase tracking-wider mb-2">Anagrafica</h3>
                <div className="bg-white/5 rounded-lg px-4">
                  <DetailRow label="Cliente" value={order.customer_name} />
                  <DetailRow label="Data ordine" value={order.order_date} />
                  <DetailRow label="Valore commessa" value={order.order_value != null ? formatKpiEur(order.order_value) : null} />
                  {order.description && (
                    <DetailRow label="Descrizione" value={order.description} />
                  )}
                  {order.notes && (
                    <DetailRow label="Note" value={order.notes} />
                  )}
                </div>
              </section>

              {/* Budget */}
              <section>
                <h3 className="text-xs font-medium text-white/40 uppercase tracking-wider mb-2">Budget</h3>
                <div className="bg-white/5 rounded-lg px-4">
                  <DetailRow label="Budget ore" value={order.budget_hours != null ? `${order.budget_hours.toFixed(1)} h` : null} />
                  <DetailRow label="Budget costi" value={order.budget_cost != null ? formatKpiEur(order.budget_cost) : null} />
                </div>
              </section>

              {/* Produzione */}
              <section>
                <h3 className="text-xs font-medium text-white/40 uppercase tracking-wider mb-2">Produzione</h3>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="bg-white/5 rounded-lg p-3 flex items-center gap-3">
                    <Clock size={16} className="text-blue-400 shrink-0" />
                    <div>
                      <p className="text-xs text-white/40">Ore totali</p>
                      <p className="text-sm font-medium text-white/90">{order.production.total_hours.toFixed(1)} h</p>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 flex items-center gap-3">
                    <DollarSign size={16} className="text-emerald-400 shrink-0" />
                    <div>
                      <p className="text-xs text-white/40">Costo totale</p>
                      <p className="text-sm font-medium text-white/90">{formatKpiEur(order.production.total_cost)}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white/5 rounded-lg px-4">
                  <DetailRow label="Ore cliente" value={`${order.production.customer_hours.toFixed(1)} h`} />
                  <DetailRow label="Ore R&D" value={`${order.production.rd_hours.toFixed(1)} h`} />
                  {order.production.cost_variance != null && (
                    <DetailRow
                      label="Scostamento costi"
                      value={
                        <span className={order.production.cost_variance >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                          {order.production.cost_variance >= 0
                            ? <TrendingUp size={12} className="inline mr-1" />
                            : <TrendingDown size={12} className="inline mr-1" />
                          }
                          {formatKpiEur(order.production.cost_variance)}
                        </span>
                      }
                    />
                  )}
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
